import React, { ChangeEvent, ForwardedRef, forwardRef, useState, KeyboardEvent } from 'react';
import cn from 'classnames';
import InputMask, { ReactInputMask } from 'react-input-mask';
import { IconNameType } from '@app/components/ui/icons/icon-name.type';
import { Button, CircleButton, CustomLabel, EasyModal, TextArea } from '@ui';

type InputType = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

type InputProps = {
  dataId?: string;
  errorCustomClassName?: string;
  firstIconDataId?: string;
  secondIconDataId?: string;
  mask?: string;
  label?: string;
  defaultValue?: string | number;
  placeholder?: string;
  disabled?: boolean;
  smallPX?: boolean;
  secondIconDisable?: boolean;
  withoutBorder?: boolean;
  iconDisable?: boolean;
  name?: string;
  secondIconClassName?: string;
  error?: string;
  errorWithoutMessage?: boolean;
  secondIconWithoutBg?: boolean;
  type?: 'email' | 'text' | 'number' | 'color' | 'password' | React.HTMLInputTypeAttribute;
  icon?: IconNameType;
  secondIcon?: IconNameType;
  iconColor?: 'primary' | 'default';
  min?: number | string;
  max?: number | string;
  pattern?: string;
  maxLength?: number;
  onClick?: () => void;
  secondOnClick?: () => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  formatChars?: { [x: string]: string };
  iconHintTitle?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  stopPropagation?: boolean;
  step?: string | number | undefined;
  autoComplete?: InputType['autoComplete'];
  popupModal?: boolean;
};

const Input = forwardRef((props: InputProps, forwardedRef) => {
  const {
    mask,
    label,
    step,
    placeholder,
    errorCustomClassName,
    smallPX,
    name,
    type = 'text',
    defaultValue,
    disabled = false,
    withoutBorder = false,
    pattern,
    error,
    icon,
    secondIcon,
    iconColor = 'default',
    secondIconClassName,
    onChange,
    secondOnClick,
    popupModal,
    errorWithoutMessage,
    onClick,
    onPaste,
    onKeyDown,
    iconHintTitle,
    stopPropagation,
    dataId,
    firstIconDataId,
    secondIconDataId,
    iconDisable,
    secondIconWithoutBg = false,
    secondIconDisable,
    ...remainingProps
  } = props;
  const [popupModalState, setPopupModalState] = useState(false);
  function onClose() {
    setPopupModalState(false);
  }

  const [, setInputValue] = useState<string | number>('');

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
    onChange?.(event);
  }
  function handleOnPaste(event: React.ClipboardEvent<HTMLInputElement>) {
    setInputValue(event.clipboardData.getData('text'));
    onPaste?.(event);
  }

  const inputClassName = ({
    error,
    disabled,
    errorWithoutMessage,
    withoutBorder,
  }: {
    error: boolean;
    disabled: boolean;
    errorWithoutMessage: boolean;
    withoutBorder: boolean;
  }) =>
    cn(
      !withoutBorder && 'border-[1px]',
      disabled ? 'text-2color bg-b_light' : 'text-0color',
      error ? 'border-basic_red' : 'border-b_dark',
      errorWithoutMessage ? 'border-basic_red' : 'border-b_dark',
      'text-sm',
      'relative',
      'bg-white',
      'rounded-defaultR',
      'flex',
      'items-center',
      'block',
      'w-full',
      'h-[34px]',
    );

  const iconColorList = {
    default: 'text-3color',
    primary: 'text-action',
    white: 'text_white',
  };

  const inputProps = {
    type,
    name,
    step,
    placeholder,
    className: `border-[0px] w-full h-[32px] ${
      smallPX ? 'px-[4px]' : 'px-[12px]'
    } outline-none outline-0 ring-0 rounded-defaultR focus:ring-0 disabled:bg-4color/[.15]`,
    onChange: handleOnChange,
    onPaste: handleOnPaste,
    disabled,
    onKeyDown: (event) => onKeyDown?.(event),
  };

  return (
    <div className="w-full relative">
      {label && (
        <div className="mb-[6px] truncate">
          <CustomLabel truncate label={label} />
        </div>
      )}
      <div
        className={inputClassName({
          error: !!error,
          disabled,
          errorWithoutMessage: !!errorWithoutMessage,
          withoutBorder,
        })}
      >
        {mask ? (
          <InputMask
            onClick={(e) => e.stopPropagation()}
            mask={mask}
            ref={forwardedRef as ForwardedRef<ReactInputMask>}
            value={defaultValue}
            {...(dataId ? { 'data-id': dataId } : {})}
            {...inputProps}
            {...remainingProps}
          />
        ) : (
          <input
            onClick={(e) => e.stopPropagation()}
            ref={forwardedRef as ForwardedRef<HTMLInputElement>}
            value={defaultValue}
            pattern={pattern}
            {...(dataId ? { 'data-id': dataId } : {})}
            {...inputProps}
            {...remainingProps}
          />
        )}
        {popupModal && (
          <div
            onClick={() => setPopupModalState(true)}
            className="pointer-events- cursor-pointer flex justify-center gap-[5px] pr-[3px] bg-transparent pl-[0px]"
          >
            <CircleButton icon="WindowIcon" size={13.5} className="text-4color" />
          </div>
        )}
        {icon && (
          <div className="flex justify-center gap-[5px] pr-[3px] bg-transparent pl-[0px]">
            <CircleButton
              hintTitle={iconHintTitle}
              dataId={firstIconDataId}
              onClick={onClick?.bind(null)}
              icon={icon}
              className={cn(iconColorList[iconColor], 'items-center flex')}
              size={14}
              disable={iconDisable}
              stopPropagation={stopPropagation}
            />
            {secondIcon && (
              <div className={`rounded-[10px] ${!secondIconWithoutBg && 'bg-action'}`}>
                <CircleButton
                  disable={secondIconDisable}
                  dataId={secondIconDataId}
                  stopPropagation={stopPropagation}
                  onClick={secondOnClick?.bind(null)}
                  icon={secondIcon}
                  className={`text-white ${secondIconClassName}`}
                  size={14}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {!!error && true && (
        <div
          className={`mt-[1px] whitespace-nowrap absolute text-sm text-red-600 dark:text-red-500 ${errorCustomClassName}`}
        >
          {error}
        </div>
      )}
      {popupModal && (
        <Modal
          onChange={handleOnChange}
          value={defaultValue}
          show={popupModalState}
          onClose={onClose}
        />
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;

const Modal = ({ show, onClose, value, onChange }) => {
  const [localState, setLocalState] = useState<React.ChangeEvent<HTMLTextAreaElement>>();

  function handleSaveChanges() {
    onChange(localState);
    onCloseLocal();
  }
  function onCloseLocal() {
    onClose();
    setLocalState(undefined);
  }
  return (
    <EasyModal
      variant="largeW1164"
      withoutContentPadding
      withoutFooter
      show={show}
      onClose={onCloseLocal}
    >
      {show && (
        <div className="relative px-[24px] pt-[24px]">
          <div>
            <TextArea
              onChange={setLocalState}
              defaultValue={localState?.target.value || value}
              height={700}
            />
          </div>
          <div className="sticky bottom-0 bg-white pb-[24px] pt-[8px] flex items-center justify-between">
            <Button fill="linked" onClick={onClose} label="Отменить" />
            <Button onClick={handleSaveChanges} label="Сохранить" />
          </div>
        </div>
      )}
    </EasyModal>
  );
};
